import {action, observable} from 'mobx';
import BookingPaymentViewModel from 'store/PaymentStore/modules/Booking/BookingPaymentViewModel';
import BookingApi from 'api/BookingApi';
import BookingReceiptModel from 'store/PaymentStore/PrintModels/BookingReceiptModel';
import i18n from 'i18n/i18n';
import BookingStickerModel from 'store/PaymentStore/PrintModels/BookingStickerModel';
import PrinterApi from 'api/PrinterApi';
import AbstractModulePaymentStore from 'store/PaymentStore/modules/AbstractModulePaymentStore';
import HarborApi from '../../../../api/HarborApi';
import ErrorLogger from '../../../../utils/errorLogger';

class BookingPaymentStore extends AbstractModulePaymentStore {
    @observable booking;
    @observable hasAccessCode = false;

    constructor(paymentStore) {
        super(paymentStore);
        this.payment = paymentStore;
    }

    @action
    getBookingRoot() {
        return this.payment.rootStore.booking;
    }

    @action
    finalizePayment(orderId) {
        if (this.payment.rootStore.isPaymentFinalized(orderId)) {
            return;
        }

        // Mark as finalized immediately to prevent race conditions
        this.payment.rootStore.markPaymentAsFinalized(orderId);

        getPaymentByOrderIdPromise(orderId)
            .then((response) => {
                if (response) {
                    let responseObject = response;
                    HarborApi.getAccessCode(orderId)
                        .then((res) => {
                            responseObject.accessCode = res.data;
                            if (res.data) {
                                this.setHasAccessCode();
                            }

                            this.setBooking(responseObject);
                            this.markPaymentAsPaid(orderId);

                            this.generatePrint(responseObject);
                            this.printSticker();
                        })
                        .catch((error) => {
                            ErrorLogger.logPaymentError(error, {
                                paymentType: 'booking',
                                orderId,
                                stage: 'get_access_code',
                                extra: {
                                    responseObject
                                }
                            });
                        });
                }
            })
            .catch((error) => {
                ErrorLogger.logPaymentError(error, {
                    paymentType: 'booking',
                    orderId,
                    stage: 'get_payment_data'
                });
            });
    }

    @action
    extractTotalByOrderId(orderId) {
        return new Promise((resolve, reject) => {
            getPaymentByOrderIdPromise(orderId)
                .then((response) => {
                    this.setPaymentTotalIndoor(response.price);
                    this.setPaymentTotalOutdoor(response.price * 100);
                    resolve(response.price);
                })
                .catch((error) => {
                    ErrorLogger.logPaymentError(error, {
                        paymentType: 'booking',
                        orderId,
                        stage: 'extract_total'
                    });
                    reject(error);
                });
        });
    }

    @action
    setHasAccessCode() {
        this.hasAccessCode = true;
    }

    @action
    setBooking(response) {
        if (response.dateFrom) {
            this.accessCode = response.accessCode;
            this.booking = new BookingPaymentViewModel(response, this.payment.rootStore.locale.locale);
            this.payment.rootStore
                .booking.setBookingFromApi(new BookingPaymentViewModel(response, this.payment.rootStore.locale.locale));
        }
    }

    @action
    markPaymentAsPaid(orderId) {
        BookingApi.markAsPaid(orderId, this.getGateway(), this.payment.rootStore.booking.fee)
            .then(() => {
                // Payment marked as paid successfully
            })
            .catch((error) => {
                ErrorLogger.logPaymentError(error, {
                    paymentType: 'booking',
                    orderId,
                    stage: 'mark_as_paid',
                    extra: {
                        gateway: this.getGateway(),
                        fee: this.payment.rootStore.booking.fee
                    }
                });
            });
    }

    @action
    generatePrint(response) {
        try {
            this.receiptPrint = new BookingReceiptModel().build(response, this.payment.rootStore, i18n);
            this.stickerPrint = new BookingStickerModel().build(response, this.payment.rootStore);
        } catch (error) {
            ErrorLogger.logPrinterError(error, {
                action: 'generate_print',
                receiptType: 'booking',
                extra: {
                    response
                }
            });
        }
    }

    printSticker() {
        if (this.payment.rootStore.terminal.hasPrinter) {
            PrinterApi.printBookingSticker(this.stickerPrint)
                .then(() => {
                    // Sticker printed successfully
                })
                .catch((error) => {
                    ErrorLogger.logPrinterError(error, {
                        action: 'print_sticker',
                        receiptType: 'booking_sticker'
                    });
                });
        }
    }

    printReceipt() {
        if (this.payment.rootStore.terminal.hasPrinter) {
            this.setIsReceiptPrinted(true);
            PrinterApi.printBookingReceipt(this.receiptPrint)
                .then(() => {
                    // Receipt printed successfully
                })
                .catch((err) => {
                    this.setIsReceiptPrinted(false);
                    ErrorLogger.logPrinterError(err, {
                        action: 'print_receipt',
                        receiptType: 'booking_receipt',
                        extra: {
                            receiptPrint: this.receiptPrint
                        }
                    });
                });
        }
    }
}

const getPaymentByOrderIdPromise = (orderId) => {
    return new Promise(resolve => {
        BookingApi.getBookingByPaymentOrderId(orderId)
            .then((res) => resolve(res.data))
            .catch((error) => {
                ErrorLogger.logApiError(error, {
                    endpoint: `/booking/payment/${orderId}`,
                    method: 'GET',
                    extra: {
                        orderId
                    }
                });
                resolve(null);
            });
    })
}

export default BookingPaymentStore;
